/* eslint-disable react/no-will-update-set-state */

import { faCog } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment-timezone';
import * as React from 'react';
import { Dispatch } from 'redux';
import { change, initialize } from 'redux-form';
import {
  alphabeticalSortingComparator,
  DateFormat,
  EventType,
  eventTypes,
  UserRole,
  UserViewModel,
} from 'stageclip-common';

import { createRequestActiveClientsAction } from '../../actions/clientActions';
import { createRequestAllUsersAction } from '../../actions/userActions';
import { ApiDataTypes } from '../../api/apiDataTypes';
import { FormId } from '../../constants/formId';
import * as FormHelper from '../../helpers/formHelper';
import { NormalizedClient } from '../../models/normalized/normalizedClient';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import { getAll } from '../../store/selectors';
import Button from '../buttons/button';
import { EmailsConfigurationGroup } from '../emailsConfiguration/emailsConfigurationGroup';
import ClipConfigurationForm from '../clipConfiguration/clipConfigurationForm';
import {
  BaseDispatchProps,
  baseMapDispatchToProps,
  baseMapStateToProps,
  BaseStateProps,
  ComponentBase,
} from '../componentBase';
import { AppDateTimeField } from '../form/appDateTimeField';
import { ToggleFieldComponent } from '../form/toggle/toggleField.component';
import { AppNumberField } from '../form/appNumberField';
import { AppSelectField } from '../form/appSelectField';
import { AppTextField } from '../form/appTextField';
import { AppTextAreaField } from '../form/appTextAreaField';
import { maxLength, maxValue, minValue, required, urlValidator } from '../form/formValidations';

import { SnapcodesContainer } from './snapcodes/SnapcodesContainer';
import styles from './baseEventForm.module.scss';

interface EventFormStateProps extends BaseStateProps {
  clients: NormalizedClient[];
  users: UserViewModel[];
  clientsLoaded: boolean;
  usersLoaded: boolean;
  formData: any;
}

interface EventFormDispatchProps extends BaseDispatchProps {
  changeFieldValue: (field: string, value: any) => void;
  resetFormKeepDirtyFields: () => void;
  loadClients: (c: BaseEventForm) => void;
  loadUsers: (c: BaseEventForm) => void;
}

interface EventFormInnerState {
  focusStartDate: boolean;
}

const notesValidators = [maxLength(1000)];
const uploadDurationValidators = [minValue(3), maxValue(300)];

export abstract class BaseEventForm<OwnProps = {}> extends ComponentBase<
  OwnProps,
  EventFormStateProps,
  EventFormDispatchProps,
  EventFormInnerState
> {
  protected constructor(props: any) {
    super(props);

    this.state = {
      focusStartDate: false,
    };
  }

  // eslint-disable-next-line camelcase
  public async UNSAFE_componentWillUpdate(props: any) {
    await super.UNSAFE_componentWillUpdate(props);

    if (FormHelper.isFormSubmitting(this.props.formData)) {
      this.setState({ focusStartDate: true });
    }

    if (this.state.focusStartDate && !FormHelper.isFormOpen(this.props.formData)) {
      this.setState({ focusStartDate: false });
    }
  }

  protected async loadData(props: any) {
    if (!props.clientsLoaded) {
      await props.loadClients(this);
    }

    if (!props.usersLoaded) {
      await props.loadUsers(this);
    }
  }

  protected dataLoaded(props: any): boolean {
    return props.clientsLoaded && props.usersLoaded;
  }

  protected getFields(): JSX.Element[] {
    const showVirtualGraduationFields =
      this.props.formData &&
      (this.props.formData.type === EventType.VirtualGraduation ||
        this.props.formData.type === EventType.LiveEvent ||
        this.props.formData.type === EventType.HybridClipsOnlyEvent ||
        this.props.formData.type === EventType.HybridFullCeremonyEvent) &&
      this.props.currentUser.role === UserRole.Admin;

    return [
      <AppSelectField
        name="clientId"
        label="Client"
        options={this.getClientsOptions()}
        validate={[required]}
        onChangeCallback={this.onChangeClientCallback}
        key="clientId"
      />,
      <AppSelectField
        options={this.getEventTypesOptions()}
        name="type"
        label="Event Type"
        validate={[required]}
        onChangeCallback={this.onChangeEventType}
        key="type"
      />,
      <div key="virtualGraduationConfig">
        {showVirtualGraduationFields && (
          <>
            <ToggleFieldComponent name="snapchat" label="Snapchat Guide" />
            <SnapcodesContainer />
            <ToggleFieldComponent name="slide" label="Slide Image and Message" />
            <ToggleFieldComponent name="survey" onChange={this.onChangeSurvey} label="Survey" />
            <ToggleFieldComponent name="vgIgnoreUploadedVideos" label="Ignore Virtual graduation videos" />
            <AppNumberField
              name="vgUploadDurationLimit"
              label="Upload Duration Limit [s]"
              key="vgUploadDurationLimit"
              validate={uploadDurationValidators}
            />

            {this.props.formData.survey && (
              <AppTextField name="surveyLink" label="Survey Generic Link" validate={[required, urlValidator]} />
            )}

            <AppTextField name="marchingOrderId" label="Marching Order Event ID" key="marchingOrderId" />
          </>
        )}
      </div>,
      <div key="invitationDeadlineDates">
        {showVirtualGraduationFields && (
          <>
            <AppDateTimeField
              name="invitationDate"
              label="Invitation Date"
              timezone={this.getClientTimezone()}
              key="invitationDate"
              dst={this.getClientDst()}
              isTimeSelect
            />
            <ToggleFieldComponent name="extendDeadlineDate" label="Silently extend deadline by 24 hours" />
            <AppDateTimeField
              name="deadlineDate"
              label="Deadline Date"
              timezone={this.getClientTimezone()}
              key="deadlineDate"
              dst={this.getClientDst()}
              isTimeSelect
            />
          </>
        )}
      </div>,
      <AppDateTimeField
        className={styles.startTime}
        name="startTime"
        label="Event Start Time"
        timezone={this.getClientTimezone()}
        validate={[required]}
        autoFocus={this.state.focusStartDate}
        key="startTime"
        dst={this.getClientDst()}
        isTimeSelect
      />,
      <AppTextField name="name" label="Event Name" key="name" />,
      showVirtualGraduationFields && <ToggleFieldComponent name="isLocked" label="Locked" />,
      <AppNumberField name="clipPrice" label="Clip Price" key="clipPrice" />,
      <AppSelectField name="assigneeId" label="Assignee" options={this.getUsersOptions()} key="assigneeId" />,
      <AppTextAreaField name="notes" label="Notes" key="notes" rows={3} validate={notesValidators} />,
      showVirtualGraduationFields && <EmailsConfigurationGroup key="emails" />,
      <div key="applyClientConfig">
        {this.props.formData && this.props.formData.clientId && (
          <Button
            className={styles.applyButton}
            onClick={this.applyClientConfig}
            type="button"
            icon={faCog}
            text="Apply client clip configuration"
          />
        )}
      </div>,
      <ClipConfigurationForm formId={FormId.EventForm} key="clipConfiguration" />,
    ];
  }

  private getClientsOptions() {
    return this.props.clients
      .sort((a: any, b: any) => alphabeticalSortingComparator(a.name, b.name))
      .map((client: NormalizedClient) => ({ value: client.id, label: client.name }));
  }

  private onChangeClientCallback = (value: any) => {
    if (this.props.formData && this.props.formData.startTime) {
      this.props.changeFieldValue(
        'startTime',
        moment
          .tz(this.props.formData.startTime, this.getClientTimezone(value.value))
          .format(DateFormat.DateTimeWithTimezone),
      );
    }

    this.props.changeFieldValue(
      'clipConfiguration',
      this.props.clients.find((client: NormalizedClient) => client.id === value.value).clipConfiguration,
    );
  };

  protected renderFields(): JSX.Element | string {
    this.applyClientConfig = this.applyClientConfig.bind(this);

    return <>{this.getFields()}</>;
  }

  protected validateForm(values: any) {
    const { startTime, invitationDate, deadlineDate } = values;
    const errors: any = {};

    if (
      invitationDate &&
      moment(deadlineDate)
        .subtract(1, 'days')
        .tz(this.getClientTimezone())
        .isSameOrBefore(invitationDate)
    ) {
      errors.deadlineDate = 'The deadline must be more than 1 day after the date of the Invitation';
    }

    if (
      deadlineDate &&
      startTime &&
      moment(deadlineDate)
        .add(1, 'days')
        .tz(this.getClientTimezone())
        .isSameOrAfter(moment(startTime))
    ) {
      errors.deadlineDate = 'Deadline date must at least 1 day before the start time';
    }

    return errors;
  }

  protected getClientTimezone(clientId?: string) {
    if (!clientId && (!this.props.formData || this.props.formData.clientId === undefined)) {
      return moment.tz.guess();
    }

    // @ts-ignore
    if (this.props.event) {
      // @ts-ignore
      return this.props.event.timezone || this.props.event.client.timezone;
    }

    return this.props.clients.find(
      (client: NormalizedClient) => client.id === (clientId || this.props.formData.clientId),
    ).timezone;
  }

  protected getClientDst(clientId?: string) {
    if (!clientId && (!this.props.formData || this.props.formData.clientId === undefined)) {
      return true;
    }

    // @ts-ignore
    if (this.props.event) {
      // @ts-ignore
      return this.props.event.client.dst;
    }

    return this.props.clients.find(
      (client: NormalizedClient) => client.id === (clientId || this.props.formData.clientId),
    ).dst;
  }

  private applyClientConfig() {
    // eslint-disable-next-line no-alert
    if (window.confirm('You are about to apply the client clip configuration. Are you sure?')) {
      this.props.changeFieldValue(
        'clipConfiguration',
        JSON.parse(
          this.props.clients.find((client: NormalizedClient) => client.id === this.props.formData.clientId)
            .clipConfiguration,
        ),
      );
    }
  }

  private getUsersOptions() {
    return this.props.users
      .sort((a: any, b: any) =>
        alphabeticalSortingComparator(`${a.lastName}-${a.firstName}`, `${b.lastName}-${b.firstName}`),
      )
      .map((user: UserViewModel) => ({ value: user.id, label: `${user.firstName} ${user.lastName}` }));
  }

  private onChangeEventType = () => {
    this.props.changeFieldValue('invitationDate', '');
    this.props.changeFieldValue('deadlineDate', '');
    this.props.changeFieldValue('snapchat', false);
    this.props.changeFieldValue('slide', false);
    this.props.changeFieldValue('survey', false);
    this.props.changeFieldValue('surveyLink', '');
    this.props.changeFieldValue('marchingOrderId', '');
    this.props.changeFieldValue('emails', {
      invitation: true,
      reminder: true,
      secondReminder: true,
      rejection: true,
      confirmation: true,
      invitationTemplateId: null,
      reminderTemplateId: null,
      secondReminderTemplateId: null,
      rejectionTemplateId: null,
      confirmationTemplateId: null,
    });
    this.props.resetFormKeepDirtyFields();
  };

  private onChangeSurvey = () => {
    this.props.changeFieldValue('surveyLink', '');
  };

  private getEventTypesOptions() {
    return eventTypes.map(type => ({ label: type, value: type }));
  }
}

export function mapBaseEventStateToProps(state: ClippingPlatformState): EventFormStateProps {
  const form = state.form[FormId.EventForm];

  return {
    ...baseMapStateToProps(state),
    clients: getAll(state.entities.clients),
    clientsLoaded: state.appStatus.allLoaded[ApiDataTypes.ActiveClients],
    formData: form ? form.values : undefined,
    users: getAll(state.entities.users).filter(({ role }) => role !== UserRole.User),
    usersLoaded: state.appStatus.allLoaded[ApiDataTypes.ActiveClients],
  };
}

export function mapBaseEventDispatchToProps(dispatch: Dispatch): EventFormDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    changeFieldValue: (field, value) => dispatch(change(FormId.EventForm, field, value)),
    resetFormKeepDirtyFields: () => dispatch(initialize(FormId.EventForm, { type: EventType.Graduation }, true)),
    loadClients: (c: BaseEventForm) => dispatch(createRequestActiveClientsAction(c, dispatch)),
    loadUsers: (c: BaseEventForm) => dispatch(createRequestAllUsersAction(c, dispatch)),
  };
}
